import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FitTable from '../components/FitTable';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import ScorecardAgentCard from '../components/ScorecardAgentCard';
import { requireAccessToken } from '../utils/authToken';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// ── Tee-box agent settings types (mirror services/teeboxAgent.js) ───────────
type Effort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

interface AgentSettings {
  model: string;
  effort: Effort;
  max_tokens: number;
  structured_output: boolean;
  web_search_max_uses: number;
  web_fetch_enabled: boolean;
  web_fetch_max_uses: number;
  web_fetch_max_content_tokens: number;
  max_resumes: number;
  per_request_seconds: number;
  total_budget_seconds: number;
  auto_run_new_courses: boolean;
}

type NumericKey =
  | 'max_tokens'
  | 'web_search_max_uses'
  | 'web_fetch_max_uses'
  | 'web_fetch_max_content_tokens'
  | 'max_resumes'
  | 'per_request_seconds'
  | 'total_budget_seconds';

type BoolKey = 'structured_output' | 'web_fetch_enabled' | 'auto_run_new_courses';

interface KnownModel { id: string; label: string; note?: string }

interface AgentSettingsInfo {
  settings: AgentSettings;
  defaults: AgentSettings;
  limits: Record<NumericKey, { min: number; max: number }>;
  effort_levels: Effort[];
  models: KnownModel[];
  overridden: string[];
  updated_at: string | null;
}

interface RunUsage {
  model?: string;
  effort?: string | null;
  max_tokens?: number;
  structured_output?: boolean;
  requests?: number;
  input_tokens?: number;
  output_tokens?: number;
  cache_creation_input_tokens?: number;
  cache_read_input_tokens?: number;
  web_search_requests?: number;
  web_fetch_requests?: number;
  elapsed_ms?: number;
}

interface AgentRun {
  run_id: number;
  course_id: number;
  course_name: string | null;
  status: string;
  trigger: string;
  model: string | null;
  usage: RunUsage | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

interface UsageInfo {
  runs: AgentRun[];
  totals: {
    runs: number;
    with_usage: number;
    input_tokens: number;
    output_tokens: number;
    cache_read_input_tokens: number;
    web_search_requests: number;
    web_fetch_requests: number;
    requests: number;
    elapsed_ms: number;
  };
}

// Numeric fields are edited as strings so a half-typed value never snaps back.
type Draft = { [K in keyof AgentSettings]: AgentSettings[K] extends boolean ? boolean : string };

const toDraft = (s: AgentSettings): Draft => ({
  model: s.model,
  effort: s.effort,
  max_tokens: String(s.max_tokens),
  structured_output: s.structured_output,
  web_search_max_uses: String(s.web_search_max_uses),
  web_fetch_enabled: s.web_fetch_enabled,
  web_fetch_max_uses: String(s.web_fetch_max_uses),
  web_fetch_max_content_tokens: String(s.web_fetch_max_content_tokens),
  max_resumes: String(s.max_resumes),
  per_request_seconds: String(s.per_request_seconds),
  total_budget_seconds: String(s.total_budget_seconds),
  auto_run_new_courses: s.auto_run_new_courses,
});

const NUMERIC_KEYS: NumericKey[] = [
  'max_tokens', 'web_search_max_uses', 'web_fetch_max_uses', 'web_fetch_max_content_tokens',
  'max_resumes', 'per_request_seconds', 'total_budget_seconds',
];

const EFFORT_HELP: Record<Effort, string> = {
  low: 'fewest thinking tokens; fine for well-documented courses',
  medium: 'default — balanced cost and thoroughness',
  high: 'roughly doubles thinking tokens vs medium',
  xhigh: 'deep reasoning; expensive on a research task',
  max: 'maximum reasoning; only when cost is no object',
};

const fmt = (n: number | undefined | null) => (n == null ? '—' : Number(n).toLocaleString());
const fmtSecs = (ms: number | undefined | null) => (ms == null ? '—' : `${Math.round(ms / 1000)}s`);
const fmtWhen = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const statusBadge = (status: string) => {
  const cls =
    status === 'confirmed' || status === 'completed' ? 'bga-badge-green'
      : status === 'pending_review' ? 'bga-badge-amber'
        : status === 'failed' || status === 'cancelled' ? 'bga-badge-red'
          : 'bga-badge-gray';
  return <span className={`bga-badge ${cls}`}>{status.replace(/_/g, ' ')}</span>;
};

const Toggle: React.FC<{ id: string; checked: boolean; onChange: (v: boolean) => void; label?: string; disabled?: boolean }> =
  ({ id, checked, onChange, label, disabled }) => (
    <div className="form-check form-switch" style={{ fontSize: 20, margin: 0, paddingLeft: '2.6em', opacity: disabled ? 0.5 : 1 }}>
      <input
        className="form-check-input"
        type="checkbox"
        role="switch"
        id={id}
        checked={checked}
        disabled={disabled}
        onChange={e => onChange(e.target.checked)}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      />
      <label
        className="form-check-label"
        htmlFor={id}
        style={{ fontSize: 13, fontWeight: 600, color: checked ? 'var(--bga-green)' : 'var(--bga-muted)', verticalAlign: 'middle' }}
      >
        {label ?? (checked ? 'On' : 'Off')}
      </label>
    </div>
  );

const sectionTitle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
  color: 'var(--bga-muted)', margin: '18px 0 10px', paddingBottom: 6, borderBottom: '1px solid var(--bga-border-soft, #eef2ef)',
};
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '14px 20px' };
const hint: React.CSSProperties = { fontSize: 11.5, color: 'var(--bga-muted)', lineHeight: 1.35 };
const errText: React.CSSProperties = { fontSize: 11.5, color: 'var(--bga-danger, #c0392b)', fontWeight: 600 };
const modifiedTag: React.CSSProperties = {
  marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#b7770d', background: '#fef9e7',
  border: '1px solid #f9e79f', borderRadius: 999, padding: '0 6px', verticalAlign: 'middle',
};

const Configuration: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [autoHandicap, setAutoHandicap] = useState(false);
  const [loading, setLoading] = useState(true);

  // Tee-box agent
  const [agentInfo, setAgentInfo] = useState<AgentSettingsInfo | null>(null);
  const [agentError, setAgentError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [customModel, setCustomModel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const scorecardToken = useMemo(() => requireAccessToken(getAccessTokenSilently), [getAccessTokenSilently]);

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const applyInfo = useCallback((info: AgentSettingsInfo) => {
    setAgentInfo(info);
    setDraft(toDraft(info.settings));
    setCustomModel(!info.models.some(m => m.id === info.settings.model));
  }, []);

  const fetchUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      const res = await axios.get<UsageInfo>(`${API_BASE_URL}/teebox-agent/usage?limit=10`, await authHeaders());
      setUsage(res.data);
    } catch (err) {
      console.error('Failed to load tee-box agent usage:', err);
    } finally {
      setUsageLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    const load = async () => {
      try {
        const cfg = await authHeaders();
        const [hc, agent] = await Promise.allSettled([
          axios.get(`${API_BASE_URL}/getConfig/automatic_handicap_calculation`, cfg),
          axios.get<AgentSettingsInfo>(`${API_BASE_URL}/teebox-agent/config`, cfg),
        ]);
        if (hc.status === 'fulfilled') setAutoHandicap(hc.value.data.config.value);
        else console.error('Failed to load config:', hc.reason);
        if (agent.status === 'fulfilled') applyInfo(agent.value.data);
        else {
          console.error('Failed to load tee-box agent settings:', agent.reason);
          setAgentError('Could not load the tee-box agent settings. Is the backend up to date (migrations applied)?');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
    fetchUsage();
  }, [authHeaders, applyInfo, fetchUsage]);

  const toggleAutoHandicap = async () => {
    const newValue = !autoHandicap;
    try {
      await axios.post(`${API_BASE_URL}/updateConfig`, { key: 'automatic_handicap_calculation', value: newValue }, await authHeaders());
      setAutoHandicap(newValue);
    } catch (err) {
      console.error('Failed to update config:', err);
    }
  };

  // ── Draft helpers ────────────────────────────────────────────────────────
  const setField = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft(prev => (prev ? { ...prev, [key]: value } : prev));
    setSaveMsg(null);
  };

  // Client-side validation mirrors the server's bounds so Save is only enabled
  // for a payload the server will accept.
  const fieldErrors = useMemo(() => {
    const errs: Partial<Record<keyof Draft, string>> = {};
    if (!draft || !agentInfo) return errs;
    if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{2,79}$/.test(draft.model.trim())) errs.model = 'Enter a Claude model id, e.g. claude-sonnet-5.';
    const nums: Partial<Record<NumericKey, number>> = {};
    for (const key of NUMERIC_KEYS) {
      const raw = draft[key].trim();
      const n = Number(raw);
      const { min, max } = agentInfo.limits[key];
      if (raw === '' || !Number.isInteger(n)) errs[key] = 'Whole number required.';
      else if (n < min || n > max) errs[key] = `Must be ${min.toLocaleString()}–${max.toLocaleString()}.`;
      else nums[key] = n;
    }
    if (nums.per_request_seconds != null && nums.total_budget_seconds != null && nums.total_budget_seconds < nums.per_request_seconds) {
      errs.total_budget_seconds = 'Must be at least the per-request cap.';
    }
    return errs;
  }, [draft, agentInfo]);

  const payload = useMemo<AgentSettings | null>(() => {
    if (!draft) return null;
    return {
      model: draft.model.trim(),
      effort: draft.effort as Effort,
      max_tokens: Number(draft.max_tokens),
      structured_output: draft.structured_output,
      web_search_max_uses: Number(draft.web_search_max_uses),
      web_fetch_enabled: draft.web_fetch_enabled,
      web_fetch_max_uses: Number(draft.web_fetch_max_uses),
      web_fetch_max_content_tokens: Number(draft.web_fetch_max_content_tokens),
      max_resumes: Number(draft.max_resumes),
      per_request_seconds: Number(draft.per_request_seconds),
      total_budget_seconds: Number(draft.total_budget_seconds),
      auto_run_new_courses: draft.auto_run_new_courses,
    };
  }, [draft]);

  const dirty = useMemo(() => {
    if (!payload || !agentInfo) return false;
    return (Object.keys(payload) as (keyof AgentSettings)[]).some(k => payload[k] !== agentInfo.settings[k]);
  }, [payload, agentInfo]);

  const isModified = (key: keyof AgentSettings) => !!payload && !!agentInfo && payload[key] !== agentInfo.defaults[key];
  const hasErrors = Object.keys(fieldErrors).length > 0;

  const save = async () => {
    if (!payload || hasErrors) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await axios.put<AgentSettingsInfo>(`${API_BASE_URL}/teebox-agent/config`, { settings: payload }, await authHeaders());
      applyInfo(res.data);
      setSaveMsg({ kind: 'ok', text: 'Saved. The next agent run uses these settings.' });
    } catch (err: any) {
      const data = err?.response?.data;
      const detail = Array.isArray(data?.details) && data.details.length ? data.details.join(' ') : data?.error;
      setSaveMsg({ kind: 'err', text: detail || 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (agentInfo) applyInfo(agentInfo);
    setSaveMsg(null);
  };

  const resetDefaults = async () => {
    if (!window.confirm('Reset every tee-box agent setting to its default?')) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await axios.delete<AgentSettingsInfo>(`${API_BASE_URL}/teebox-agent/config`, await authHeaders());
      applyInfo(res.data);
      setSaveMsg({ kind: 'ok', text: 'Settings reset to defaults.' });
    } catch (err) {
      console.error('Failed to reset tee-box agent settings:', err);
      setSaveMsg({ kind: 'err', text: 'Failed to reset settings.' });
    } finally {
      setSaving(false);
    }
  };

  // ── Field renderers ──────────────────────────────────────────────────────
  const numberField = (key: NumericKey, label: string, help: string, opts: { disabled?: boolean; step?: number } = {}) => {
    if (!draft || !agentInfo) return null;
    const { min, max } = agentInfo.limits[key];
    const err = fieldErrors[key];
    return (
      <div className="bga-field" key={key}>
        <label className="bga-label" htmlFor={`ta-${key}`}>
          {label}
          {isModified(key) && <span style={modifiedTag}>modified</span>}
        </label>
        <input
          id={`ta-${key}`}
          className="bga-input"
          type="number"
          min={min}
          max={max}
          step={opts.step ?? 1}
          value={draft[key]}
          disabled={opts.disabled}
          onChange={e => setField(key, e.target.value)}
          style={{ ...(err ? { borderColor: 'var(--bga-danger, #c0392b)' } : {}), ...(opts.disabled ? { opacity: 0.55 } : {}) }}
        />
        {err ? <span style={errText}>{err}</span> : (
          <span style={hint}>
            {help} · default {agentInfo.defaults[key].toLocaleString()} · {min.toLocaleString()}–{max.toLocaleString()}
          </span>
        )}
      </div>
    );
  };

  const boolField = (key: BoolKey, label: string, help: string, opts: { disabled?: boolean } = {}) => {
    if (!draft || !agentInfo) return null;
    return (
      <div className="bga-field" key={key}>
        <span className="bga-label">
          {label}
          {isModified(key) && <span style={modifiedTag}>modified</span>}
        </span>
        <Toggle id={`ta-${key}`} checked={draft[key]} disabled={opts.disabled} onChange={v => setField(key, v)} />
        <span style={hint}>{help} · default {agentInfo.defaults[key] ? 'on' : 'off'}</span>
      </div>
    );
  };

  // ── Tee-box agent card ───────────────────────────────────────────────────
  const renderAgentCard = () => {
    if (agentError) {
      return (
        <div className="bga-card">
          <h2 className="bga-card-title">Teebox Agent Configuration</h2>
          <p className="bga-muted" style={{ margin: 0 }}>{agentError}</p>
        </div>
      );
    }
    if (!draft || !agentInfo) {
      return (
        <div className="bga-card">
          <h2 className="bga-card-title">Teebox Agent Configuration</h2>
          <p className="bga-muted" style={{ margin: 0 }}>Loading…</p>
        </div>
      );
    }
    const overrides = agentInfo.overridden.length;
    const knownModel = agentInfo.models.find(m => m.id === draft.model);
    const isHaiku = /^claude-haiku/.test(draft.model);
    const t = usage?.totals;

    return (
      <div className="bga-card">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <h2 className="bga-card-title" style={{ marginBottom: 4 }}>
              Teebox Agent Configuration
              <span className={`bga-badge ${overrides ? 'bga-badge-amber' : 'bga-badge-gray'}`} style={{ marginLeft: 10, verticalAlign: 'middle' }}>
                {overrides ? `${overrides} override${overrides === 1 ? '' : 's'}` : 'defaults'}
              </span>
            </h2>
            <div className="bga-muted" style={{ fontSize: 13 }}>
              How the AI tee-box researcher (Courses → Find tee boxes / scorecard upload) runs, and what it may spend.
              Changes apply to the next run; no restart needed.
              {agentInfo.updated_at && <> Last saved {fmtWhen(agentInfo.updated_at)}.</>}
            </div>
          </div>
        </div>

        {/* Model & reasoning */}
        <div style={sectionTitle}>Model &amp; reasoning</div>
        <div style={grid}>
          <div className="bga-field" style={{ gridColumn: 'span 2' }}>
            <label className="bga-label" htmlFor="ta-model">
              Model
              {isModified('model') && <span style={modifiedTag}>modified</span>}
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select
                id="ta-model"
                className="bga-select"
                style={{ flex: '1 1 220px' }}
                value={customModel ? '__custom__' : draft.model}
                onChange={e => {
                  if (e.target.value === '__custom__') { setCustomModel(true); return; }
                  setCustomModel(false);
                  setField('model', e.target.value);
                }}
              >
                {agentInfo.models.map(m => <option key={m.id} value={m.id}>{m.label} — {m.id}</option>)}
                <option value="__custom__">Custom model id…</option>
              </select>
              {customModel && (
                <input
                  className="bga-input"
                  style={{ flex: '1 1 220px', ...(fieldErrors.model ? { borderColor: 'var(--bga-danger, #c0392b)' } : {}) }}
                  placeholder="claude-…"
                  value={draft.model}
                  onChange={e => setField('model', e.target.value)}
                />
              )}
            </div>
            {fieldErrors.model
              ? <span style={errText}>{fieldErrors.model}</span>
              : <span style={hint}>{knownModel?.note ? `${knownModel.note} · ` : ''}default {agentInfo.defaults.model}</span>}
          </div>

          <div className="bga-field">
            <label className="bga-label" htmlFor="ta-effort">
              Reasoning effort
              {isModified('effort') && <span style={modifiedTag}>modified</span>}
            </label>
            <select
              id="ta-effort"
              className="bga-select"
              value={draft.effort}
              disabled={isHaiku}
              onChange={e => setField('effort', e.target.value)}
              style={isHaiku ? { opacity: 0.55 } : undefined}
            >
              {agentInfo.effort_levels.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            <span style={hint}>
              {isHaiku ? 'Haiku has no effort control — ignored.' : EFFORT_HELP[draft.effort as Effort] ?? ''} · default {agentInfo.defaults.effort}
            </span>
          </div>

          {boolField('structured_output', 'Structured JSON output',
            'Ask the API to return the tee-box JSON schema directly. Prevents the "prose around the JSON" parse failures; falls back automatically if the model rejects it')}
          {boolField('auto_run_new_courses', 'Run automatically for new courses',
            'When a course is created with no tee boxes, start a research run in the background')}
        </div>

        {/* Token usage */}
        <div style={sectionTitle}>Token usage</div>
        <div style={grid}>
          {numberField('max_tokens', 'Max output tokens / request', 'Cap on the answer (JSON + reasoning). Too low truncates hole-by-hole data', { step: 500 })}
          {numberField('web_search_max_uses', 'Web searches / request', 'Each search returns snippets the model must read (input tokens)')}
          {boolField('web_fetch_enabled', 'Fetch full pages',
            'Open scorecard pages (course site, GolfPass, BlueGolf) instead of relying on search snippets. Costs more input tokens, finds hole-by-hole data')}
          {numberField('web_fetch_max_uses', 'Page fetches / request', 'How many pages the model may open', { disabled: !draft.web_fetch_enabled })}
          {numberField('web_fetch_max_content_tokens', 'Max tokens per fetched page', 'Each page is truncated to this many tokens before the model reads it', { disabled: !draft.web_fetch_enabled, step: 500 })}
          {numberField('max_resumes', 'Max tool-loop resumes', 'Continuations when the model is still using web tools; each resume re-sends the whole context')}
        </div>

        {/* Time limits */}
        <div style={sectionTitle}>Time limits</div>
        <div style={grid}>
          {numberField('per_request_seconds', 'Per-request cap (seconds)', 'A single streamed request is aborted after this', { step: 10 })}
          {numberField('total_budget_seconds', 'Whole-run budget (seconds)', 'The run fails once this is exceeded, including the search-only retry', { step: 30 })}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
          <button className="bga-btn bga-btn-primary" onClick={save} disabled={!dirty || hasErrors || saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </button>
          <button className="bga-btn bga-btn-secondary" onClick={discard} disabled={!dirty || saving}>Discard changes</button>
          <button className="bga-btn bga-btn-secondary" onClick={resetDefaults} disabled={saving || (overrides === 0 && !dirty)} style={{ marginLeft: 'auto' }}>
            Reset to defaults
          </button>
        </div>
        {saveMsg && (
          <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: saveMsg.kind === 'ok' ? 'var(--bga-green)' : 'var(--bga-danger, #c0392b)' }}>
            {saveMsg.text}
          </div>
        )}
        {dirty && !saveMsg && !hasErrors && (
          <div className="bga-muted" style={{ marginTop: 10, fontSize: 12.5 }}>Unsaved changes.</div>
        )}

        {/* Recent usage */}
        <div style={{ ...sectionTitle, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>Recent runs · token usage</span>
          <button className="bga-btn bga-btn-secondary bga-btn-sm" onClick={fetchUsage} disabled={usageLoading} style={{ marginLeft: 'auto', textTransform: 'none', letterSpacing: 0 }}>
            {usageLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        {!usage || usage.runs.length === 0 ? (
          <div className="bga-empty" style={{ padding: '14px 0' }}>No agent runs yet. Usage is recorded from the next run.</div>
        ) : (
          <>
            <FitTable card>
              <table className="bga-table bga-table--sm">
                <thead>
                  <tr>
                    <th>Run</th>
                    <th>Course</th>
                    <th>When</th>
                    <th>Status</th>
                    <th>Model · effort</th>
                    <th style={{ textAlign: 'right' }} title="API requests in the run (including tool-loop resumes)">Req</th>
                    <th style={{ textAlign: 'right' }} title="Input tokens (cache reads in parentheses)">In</th>
                    <th style={{ textAlign: 'right' }} title="Output tokens (answer + thinking)">Out</th>
                    <th style={{ textAlign: 'right' }} title="Web searches / page fetches">Search · Fetch</th>
                    <th style={{ textAlign: 'right' }}>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.runs.map(r => {
                    const u = r.usage;
                    return (
                      <tr key={r.run_id}>
                        <td className="bga-muted nowrap">#{r.run_id} <span style={{ fontSize: '0.85em' }}>({r.trigger})</span></td>
                        <td>{r.course_name ?? `course ${r.course_id}`}</td>
                        <td className="bga-muted" style={{ whiteSpace: 'nowrap' }}>{fmtWhen(r.created_at)}</td>
                        <td title={r.error ?? undefined}>{statusBadge(r.status)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {u?.model ?? r.model ?? '—'}
                          {u?.effort ? <span className="bga-muted"> · {u.effort}</span> : null}
                          {u && u.structured_output === false ? <span className="bga-muted" title="Structured output was off for this run"> · free-text</span> : null}
                        </td>
                        {u ? (
                          <>
                            <td style={{ textAlign: 'right' }}>{fmt(u.requests)}</td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {fmt(u.input_tokens)}
                              {u.cache_read_input_tokens ? <span className="bga-muted"> ({fmt(u.cache_read_input_tokens)})</span> : null}
                            </td>
                            <td style={{ textAlign: 'right' }}>{fmt(u.output_tokens)}</td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(u.web_search_requests)} · {fmt(u.web_fetch_requests)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtSecs(u.elapsed_ms)}</td>
                          </>
                        ) : (
                          <td colSpan={5} className="bga-muted" style={{ fontSize: '0.92em' }}>no usage recorded (run predates usage tracking)</td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                {t && t.with_usage > 0 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700 }}>
                      <td colSpan={5}>Totals · last {t.runs} run{t.runs === 1 ? '' : 's'} ({t.with_usage} with usage)</td>
                      <td style={{ textAlign: 'right' }}>{fmt(t.requests)}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {fmt(t.input_tokens)}
                        {t.cache_read_input_tokens ? <span className="bga-muted" style={{ fontWeight: 400 }}> ({fmt(t.cache_read_input_tokens)})</span> : null}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmt(t.output_tokens)}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(t.web_search_requests)} · {fmt(t.web_fetch_requests)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtSecs(t.elapsed_ms)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </FitTable>
            <div style={{ ...hint, marginTop: 8 }}>
              In = input tokens billed per request (every tool-loop resume re-sends the accumulated context, so searches and fetches show up here).
              Out = output tokens (the JSON answer plus reasoning). Web searches and fetches are billed separately from tokens.
            </div>
          </>
        )}
      </div>
    );
  };

  if (loading) return <div className="bga-page"><p className="bga-muted">Loading configuration…</p></div>;

  return (
    <div className="bga-page" style={{ maxWidth: 920 }}>
      <div className="bga-page-header">
        <div>
          <h1 className="bga-h1">Configuration</h1>
          <p className="bga-sub">League-wide settings.</p>
        </div>
      </div>

      <div className="bga-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Automatic Handicap Calculation · league default</div>
            <div className="bga-muted" style={{ fontSize: 13, marginTop: 2 }}>
              When on, player handicaps update automatically as scores are posted. This is the
              default for every season; a season can override it (On/Off) on the Seasons page.
            </div>
          </div>
          <Toggle id="autoHandicapSwitch" checked={autoHandicap} onChange={toggleAutoHandicap} />
        </div>
      </div>

      {renderAgentCard()}

      <ScorecardAgentCard getToken={scorecardToken} />
    </div>
  );
};

export default Configuration;
